import React, { useMemo } from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import TooltipV2 from "@ui/TooltipV2";
import { InformationLine } from "@ui/IconV2/react";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import t from "../../../../i18n";
import { groupBy } from "diagnostics-lib/iterable";
import WidgetCard from "../WidgetCard";
import CountPriorityBar from "./CountPriorityBar";
import { FaultEventLoading } from "../../Events/components/FaultEventLoading";
import classNames from "classnames";
import { useSettings } from "../../../../api/useSettings";
import type { Event } from "../../../../api/useEvents";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import { EVENT_PRIORITIES } from "../../Events/utils/event";
import { getEventPriority } from "../../../../domain/event";

interface EventPriorityWidgetProps {
  projectId: string;
  events: Event[];
  loading?: boolean;
  className?: string;
}

const EventPriorityWidget = ({
  loading = false,
  projectId,
  events,
  className,
}: EventPriorityWidgetProps) => {
  const { isStreaming } = useActiveAnalysesStream(projectId);

  const activeEvents = useMemo(() => events.filter(event => event.metadata.active), [events]);
  const activeEventCountByPriority = useMemo(
    () => groupBy(activeEvents, getEventPriority).mapValues(values => values.length),
    [activeEvents],
  );
  const isMediumPriorityDisabled = useSettings().FAULT_EVENTS_DISABLE_MEDIUM_PRIORITY;
  const priorities = EVENT_PRIORITIES.filter(p => p !== "MEDIUM" || !isMediumPriorityDisabled);

  return (
    <WidgetCard className={classNames(eventPriorityWidget, className)}>
      <div className={wrapperTitle}>
        <div className={titleWithIcon}>
          <Text type="body1SemiBold">
            {t("project.fault_event.live.widget.event_priority.title")}
          </Text>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                className={featureOverlay}
                text={t("project.fault_event.live.widget.event_priority.tooltip")}
              />
            }
          >
            <span className={centerIcon}>
              <InformationLine size="s" color={colors.carbon800} />
            </span>
          </TooltipV2>
        </div>
      </div>
      <div className={eventPriorityWidget_content}>
        {loading || isStreaming ? (
          <FaultEventLoading />
        ) : activeEvents.length === 0 ? (
          <Text className={noActiveEvents} type="body2">
            {t("project.fault_event.live.widget.event_priority.empty")}
          </Text>
        ) : (
          <div className={priorityWidget}>
            {priorities.map(priority => (
              <CountPriorityBar
                key={priority}
                priority={priority}
                value={activeEventCountByPriority.get(priority) ?? 0}
                total={activeEvents.length}
              />
            ))}
          </div>
        )}
      </div>
    </WidgetCard>
  );
};

export default EventPriorityWidget;

//#region CSS
const eventPriorityWidget = css`
  min-width: 210px;
  max-width: 280px;

  display: flex;
  height: 100%;
  width: 100%;
`;
const eventPriorityWidget_content = css`
  flex: 1;
  display: flex;
  align-items: center;
`;
const featureOverlay = css`
  max-width: 300px;
`;

const noActiveEvents = css`
  color: ${colors.carbon500};
`;
const priorityWidget = css`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-auto-rows: 32px;
  row-gap: ${spacings.xs};
  column-gap: ${spacings.xs};
  align-items: center;
  width: 100%;
`;
const wrapperTitle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacings.xs};
  width: 100%;
`;
const titleWithIcon = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon800};
  flex-wrap: nowrap;

  & > :nth-child(1) {
    white-space: nowrap;
  }
`;
const centerIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
//#endregion CSS
